import { createTheme, type MantineColorsTuple } from '@mantine/core';

// Palette intent — WWAAVE Brand Pack v3 "Earth meets water" (see BRAND_PACK.md):
// - `brand` is Clay (#D2602E at index 6, the primary — the artist, the spark, the
//   road). Warm earth, deliberately chosen over corporate-cool blues/slates.
// - `tide` is the teal secondary (#2A7E78 at index 6 — the water, the venue, the
//   calm of trust and safety). Clay + Tide together IS the brand ("connection is
//   a meeting, not a colour"); it is also the logo (clay dot + teal ripples).
// - `sun` is the warm-gold accent (#E9A23B) for gradient endpoints + on-ink
//   highlights. Never a white-text-button or standalone text colour on paper.
// - `ink` is the warm neutral text/surface ramp, anchored to Ink #211C16 (see
//   CLAUDE.md "ink colour tuple direction": .8/.9 = near-black text, .0/.1 =
//   near-white surfaces in light mode; warmth.css inverts under dark mode).
// Ramps are oklch-anchored so the pack hex sits at index 6 — identical to what the
// runtime generatePalette() injector produces, so admin overrides stay consistent.
// The warm light-mode treatment + branded dark mode live in warmth.css.

const brand: MantineColorsTuple = [
  '#fff4f0',
  '#ffdacc',
  '#ffbfa6',
  '#ffa27e',
  '#fb8555',
  '#e67242',
  '#d2602e',
  '#a33b00',
  '#6c2300',
  '#3a0c00',
];

const tide: MantineColorsTuple = [
  '#ddfffb',
  '#a0f1e9',
  '#88d9d1',
  '#71c1ba',
  '#5aaaa4',
  '#43948d',
  '#2a7e78',
  '#00605b',
  '#00413d',
  '#002522',
];

const sun: MantineColorsTuple = [
  '#fff5e9',
  '#ffe7ca',
  '#ffd8a9',
  '#ffc985',
  '#ffb95b',
  '#f5ad48',
  '#e9a23b',
  '#aa6e00',
  '#684100',
  '#2e1900',
];

const ink: MantineColorsTuple = [
  '#fff9f2',
  '#f5efe8',
  '#e3ddd6',
  '#c9c3bc',
  '#a39d96',
  '#807b74',
  '#615b55',
  '#45403a',
  '#2e2924',
  '#211c16',
];

export const theme = createTheme({
  primaryColor: 'brand',
  // Clay #D2602E lives at index 6 — make it the primary so buttons ARE the spark
  // (white label, 3.85:1 AA-large). Dark mode lifts to index 5 for contrast on ink.
  primaryShade: { light: 6, dark: 5 },
  autoContrast: true,
  luminanceThreshold: 0.3,
  // Default near-black is warm Ink, not cool #000 (brand-pure neutrals).
  black: '#211c16',
  // BRAND-ONLY PALETTE. The Brand Pack v3 colours are the ONLY palette — there is
  // no stock red/green/teal/blue/yellow/grey. Mantine's built-in colour NAMES are
  // overridden with brand ramps so any `color="red"` / `c="gray"` etc. resolves to
  // a brand colour AND Mantine regenerates every filled/light/outline variant from
  // the brand ramp. The pack has no danger colour, so red maps to Clay (the spark
  // carries intensity); success/teal/blue map to Tide; warning to Sun; neutral grey
  // to the warm Ink ramp. (See BRAND_PACK.md + the user's brand-purity directive.)
  colors: {
    brand,
    tide,
    sun,
    ink,
    red: brand,
    green: tide,
    teal: tide,
    // info / in-progress (trial, under-review) -> neutral warm Ink, so it stays
    // distinct from success (Tide), error (Clay) and warning (Sun). The brand has
    // only three chromatic hues, so info reads as neutral rather than a 4th colour.
    blue: ink,
    yellow: sun,
    gray: ink,
    // Remaining stock names remapped too, so no off-brand colour can ever leak in
    // (warm -> Clay/Sun, cool -> Tide). `dark` is left to warmth.css (the dark ramp).
    orange: sun,
    pink: brand,
    grape: tide,
    violet: tide,
    indigo: tide,
    cyan: tide,
    lime: tide,
  },

  // Poppins carries everything you read, body and headlines alike. JetBrains
  // Mono handles small DIY labels, and Darker Grotesque is reserved for the
  // logo wordmark (applied in WwaaveLogo). The stacks live in warmth.css as
  // --wwaave-font-* so there is ONE lever for type, and since the admin font
  // picker was retired (2026-08-18) that lever is the only one.
  fontFamily: 'var(--wwaave-font-body)',
  fontFamilyMonospace: "'JetBrains Mono', ui-monospace, SFMono-Regular, 'Menlo', monospace",

  headings: {
    fontFamily: 'var(--wwaave-font-display)',
    fontWeight: '700',
    sizes: {
      h1: { fontSize: '2.25rem', lineHeight: '1.15', fontWeight: '700' },
      h2: { fontSize: '1.75rem', lineHeight: '1.2', fontWeight: '700' },
      h3: { fontSize: '1.375rem', lineHeight: '1.25', fontWeight: '700' },
      h4: { fontSize: '1.125rem', lineHeight: '1.35', fontWeight: '600' },
      h5: { fontSize: '1rem', lineHeight: '1.4', fontWeight: '600' },
      h6: { fontSize: '0.875rem', lineHeight: '1.45', fontWeight: '600' },
    },
  },

  other: {
    headingLetterSpacing: '-0.01em',
    // The warm "spark + sun" gradient — covers, the header accent line, CTAs.
    brandGradient: { from: 'brand.6', to: 'sun.6', deg: 35 },
    // The Clay <-> Tide "meeting" — reserved for the logo + hero / cover key moments.
    meetingGradient: { from: 'brand.6', to: 'tide.6', deg: 35 },
  },

  defaultRadius: 'lg',
  radius: {
    xs: '4px',
    sm: '6px',
    md: '8px',
    lg: '12px',
    xl: '16px',
  },

  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
  },

  focusRing: 'auto',

  components: {
    Button: {
      defaultProps: {
        size: 'md',
        color: 'brand.6',
        radius: 'xl',
      },
    },
    Anchor: {
      defaultProps: {
        underline: 'hover',
      },
      styles: {
        root: {
          color: 'light-dark(var(--mantine-color-brand-7), var(--mantine-color-brand-4))',
        },
      },
    },
    Title: {
      styles: {
        root: {
          letterSpacing: '-0.01em',
        },
      },
    },
    Card: {
      defaultProps: {
        radius: 'lg',
      },
      styles: {
        root: {
          transition: 'transform 200ms ease, box-shadow 200ms ease',
        },
      },
    },
    Paper: {
      defaultProps: {
        radius: 'lg',
      },
    },
  },
});
